'use client';

import { useIntl } from 'react-intl';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { useEffect, useState } from 'react';
import { Slider } from '@packages/shared/src/ui/Slider/Slider';
import { useInstallmentCalculatorTracking } from '../../../components/Tracking/hooks/useInstallmentCalculatorTracking/useInstallmentCalculatorTracking';
import { SliderMessages } from '../messages';
import { SelectedInstallmentDisplay } from '../SelectedInstallmentDisplay/SelectedInstallmentDisplay';
import type { InstallmentSelectedHandler } from '../Slider.client.types';

export type LinearSliderProps = {
  /**
   * the viable installments array from the response
   * used to get min and max value for slider calculation
   */
  viableInstallments: number[];
  /**
   * the number of installments from the response
   * used to display over the LinearSlider
   */
  numberOfInstallments: number;
  /**
   * a handler function triggert on LinearSlider mouseup
   */
  installmentSelectedHandler: InstallmentSelectedHandler;
};

/**
 * linear slider atom
 *
 * atom to display the linear slider
 *
 * */
export const LinearSlider = ({
  viableInstallments,
  numberOfInstallments,
  installmentSelectedHandler,
}: LinearSliderProps) => {
  const { formatMessage } = useIntl();
  const {
    installmentCalculator: { hasSpecialCreditColors },
  } = useConfig();
  const [installment, setInstallment] =
    useState<LinearSliderProps['numberOfInstallments']>(numberOfInstallments);
  const { trackSliderClick } = useInstallmentCalculatorTracking();

  useEffect(() => {
    setInstallment(numberOfInstallments);
  }, [numberOfInstallments]);

  const minInstallments = viableInstallments[0];
  const maxInstallments = viableInstallments[viableInstallments.length - 1];

  const onChangeCommittedHandler = (value: number) => {
    installmentSelectedHandler(value);
  };

  return (
    <div>
      <div className="mb-1.5">
        <SelectedInstallmentDisplay
          unit={formatMessage(SliderMessages.Month, {
            installment,
          })}
          selectedInstallment={installment}
        />
      </div>
      <Slider
        value={[installment]}
        min={minInstallments}
        max={maxInstallments}
        onValueChange={(value) => setInstallment(value[0])}
        onValueCommit={(value) => {
          onChangeCommittedHandler(value[0]);
          trackSliderClick(value.toString());
        }}
        color={hasSpecialCreditColors ? 'additional' : 'primary'}
      />
      <div className="mt-1 flex flex-nowrap justify-between">
        <span>
          {`${minInstallments} ${formatMessage(SliderMessages.Month, {
            installment: minInstallments,
          })}`}
        </span>
        <span>
          {`${maxInstallments} ${formatMessage(SliderMessages.Month, {
            installment: maxInstallments,
          })}`}
        </span>
      </div>
    </div>
  );
};
